import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { candidates } from "../services/api";

const buildInitialState = (candidate, user) => ({
  candidateId: candidate?.id || candidate?._id || "",
  fullName: candidate?.name || "",
  phone: user?.phone || "",
  email: user?.email || "",
  aadhaarLast4: "",
  idProof: null,
  selfieWithId: null,
});

const CandidateClaimForm = ({
  candidate,
  existingClaim,
  onSuccess,
  onCancel,
  submitLabel = "Submit Claim",
}) => {
  const { isAuthenticated, user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState(() => buildInitialState(candidate, user));

  useEffect(() => {
    setForm(buildInitialState(candidate, user));
  }, [candidate, user]);

  const isExistingOwner = useMemo(() => {
    const currentCandidateId = user?.candidateProfile?._id || user?.candidateProfile;
    return Boolean(
      currentCandidateId &&
        candidate?.id &&
        String(currentCandidateId) === String(candidate.id),
    );
  }, [candidate, user]);

  const isAlreadySubmitted =
    existingClaim?.status === "pending" || existingClaim?.status === "approved";

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    setForm((current) => ({ ...current, [name]: files?.[0] || null }));
  };

  const validate = () => {
    if (!isAuthenticated) {
      toast.info("Please log in to claim this candidate profile.");
      return false;
    }

    if (candidate?.isClaimed && !isExistingOwner) {
      toast.info("This candidate profile is already claimed.");
      return false;
    }

    if (isAlreadySubmitted) {
      toast.info("You have already submitted a claim for this candidate.");
      return false;
    }

    const requiredFields = ["fullName", "phone", "email"];
    const missingField = requiredFields.find((field) => !String(form[field] || "").trim());

    if (missingField) {
      toast.error("Please fill all required fields.");
      return false;
    }

    if (!/^\d{10}$/.test(form.phone.trim())) {
      toast.error("Phone number must be a valid 10-digit number.");
      return false;
    }

    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return false;
    }

    if (form.aadhaarLast4 && !/^\d{4}$/.test(form.aadhaarLast4.trim())) {
      toast.error("Aadhaar last 4 must contain exactly 4 digits.");
      return false;
    }

    if (!form.idProof || !form.selfieWithId) {
      toast.error("Upload both your ID proof and your selfie holding the ID.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = new FormData();
    payload.append("candidateId", candidate?.id || candidate?._id || "");
    payload.append("fullName", form.fullName.trim());
    payload.append("email", form.email.trim());
    payload.append("phone", form.phone.trim());
    payload.append("aadhaarLast4", form.aadhaarLast4.trim());
    payload.append("position", candidate?.position || "");
    payload.append("party", candidate?.party || "");
    payload.append("state", candidate?.state || "");
    payload.append("district", candidate?.district || "");
    payload.append("constituency", candidate?.constituency || "");
    payload.append("idProof", form.idProof);
    payload.append("selfieWithId", form.selfieWithId);

    console.log("[CandidateClaimForm] submitClaim payload", {
      apiBaseUrl: process.env.REACT_APP_API_URL || "http://localhost:5000",
      candidateId: candidate?.id || candidate?._id || "",
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      aadhaarLast4: form.aadhaarLast4.trim(),
      position: candidate?.position || "",
      party: candidate?.party || "",
      state: candidate?.state || "",
      district: candidate?.district || "",
      constituency: candidate?.constituency || "",
      hasIdProof: Boolean(form.idProof),
      hasSelfieWithId: Boolean(form.selfieWithId),
      hasToken: Boolean(localStorage.getItem("token")),
    });

    try {
      setSubmitting(true);
      setSubmitError("");
      const response = await candidates.submitClaim(payload);
      console.log("[CandidateClaimForm] submitClaim success", response.data);
      toast.success(response.data.message || "Ownership claim submitted successfully.");
      onSuccess?.(response.data.claim);
      setForm(buildInitialState(candidate, user));
    } catch (error) {
      console.error("Candidate claim submission failed:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
      });
      const nextError =
        error.response?.data?.message || "Failed to submit ownership claim.";
      setSubmitError(nextError);
      toast.error(nextError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-[1.75rem] border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-green-50 p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
          Claiming Profile
        </p>
        <h3 className="mt-2 text-xl font-bold text-slate-900">
          {candidate?.name || "Candidate"}
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          {candidate?.position || "Candidate"} for {candidate?.constituency || "Unknown constituency"},{" "}
          {candidate?.state || "Unknown state"}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">Full Name</label>
          <input
            type="text"
            name="fullName"
            value={form.fullName}
            onChange={handleChange}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-400"
            required
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-400"
            required
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">Phone</label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-400"
            required
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">Aadhaar Last 4</label>
          <input
            type="text"
            name="aadhaarLast4"
            value={form.aadhaarLast4}
            onChange={handleChange}
            maxLength={4}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-400"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">
            Government ID Proof
          </label>
          <input
            type="file"
            name="idProof"
            accept="image/png,image/jpeg,application/pdf"
            onChange={handleFileChange}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none file:mr-3 file:rounded-full file:border-0 file:bg-orange-100 file:px-3 file:py-2"
            required
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">
            Selfie Holding ID
          </label>
          <input
            type="file"
            name="selfieWithId"
            accept="image/png,image/jpeg"
            onChange={handleFileChange}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none file:mr-3 file:rounded-full file:border-0 file:bg-green-100 file:px-3 file:py-2"
            required
          />
        </div>
      </div>

      {isAlreadySubmitted && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Your existing claim status is <span className="font-semibold">{existingClaim.status}</span>.
          Another submission is disabled for now.
        </div>
      )}

      {submitError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {submitError}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting || isAlreadySubmitted}
          className="rounded-full bg-gradient-to-r from-orange-500 to-green-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200/70 transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting
            ? "Submitting..."
            : isAlreadySubmitted
              ? "Already Submitted"
              : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default CandidateClaimForm;
